<script setup>
import { ref, watch, onMounted } from "vue";
import axios from "axios";
import forestreeHelpers from "@/helpers/forestreeHelpers";
import Avatar from "vue3-avatar";
import Popper from "vue3-popper";

const props = defineProps({
  work: {
    type: Object,
    required: true
  },
  show: {
    type: Boolean,
    default: false
  },
  users: {
    type: Object,
    required: true
  },
  assignTypeId: {
    type: [Number,String],
    required: false
  },
  permissions: {
    type: Object,
    required: true
  },
  teams: {
    type: Array,
    required: true
  },
  permitted: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:show', 'workAssigned']);

const showAssignSelect = ref(false);
const assignList = ref([]);
const msg = ref(null);
const selectTeam = ref(null);
const externalMsg = ref(null);
const popover = ref(null);
const selectedUser = ref(null);
const loading = ref(false);
const error = ref(null);

onMounted(() => {
    selectTeam.value = null;
    if (props.assignTypeId === "4") {
        assignList.value = props.work.stump_assignments;
    } else {
        assignList.value = props.work.assignments;
    }

});

const close = () => {
    selectTeam.value = null;
    externalMsg.value = null;
    // if (popover.value) {
    //     popover.value.close();
    // }
};

const onChange = (uid, tid = null) => {
    externalMsg.value = "Sending... please wait.";
    axios
        .post("/api/assign/create", {
            taskId: props.work.id,
            assignType: props.assignTypeId,
            userId: uid,
            teamId: tid,
        })
        .then((response) => {
            selectTeam.value = null;
            externalMsg.value = null;
            if (response.data.assignment) {
                assignList.value.push(response.data.assignment);
                msg.value = null;
            } else {
                msg.value = "Already assigned";
            }
            showAssignSelect.value = false;
        })
        .catch((error) => {
            externalMsg.value = null;
            console.log(error);
        });
};

const unassign = (assign, idx) => {
    axios
        .post("/api/assign/remove", {
            assignmentId: assign.id,
        })
        .then((response) => {
            if (response.data.assignment === "deleted") {
                assignList.value.splice(idx, 1);
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

watch(
    () => props.work,
    (newVal) => {
        selectTeam.value = null;
        if (props.assignTypeId === "4") {
            assignList.value = newVal.stump_assignments;
        } else {
            assignList.value = newVal.assignments;
        }
    },
    { deep: true }
);

const assignWork = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    const response = await axios.post(`/api/works/${props.work.id}/assign`, {
      user_id: selectedUser.value
    });
    
    emit('workAssigned', response.data);
    emit('update:show', false);
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to assign work';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
    <div class="td pl-2 py-2 items-center text-slate-500">
        <div v-if="assignList" class="flex flex-wrap items-center">
            <span v-for="(assign, idx) in assignList" :key="idx">
                <popper
                    placement="top"
                    offset-distance="10"
                >
                    <template #content>
                            <div class="p-4 bg-white shadow-lg rounded-lg">
                                <p class="text-slate-600 text-center">
                                    <span v-if="assign.assigned_to">{{ assign.assigned_to.name }}</span>
                                    <span v-if="assign.assigned_team">{{ assign.assigned_team.name }}</span>
                                    <button
                                        v-if="permissions.work_can_assign || permitted"
                                        @click="
                                            unassign(assign, idx);
                                        "
                                        class="ml-2 text-xs bg-slate-200 text-slate-700 hover:bg-red-600 hover:text-white py-1 px-2 rounded-full"
                                    >
                                        Unassign
                                    </button>
                                </p>
                            </div>
                    </template>

                    <avatar
                        v-if="assign.assigned_team"
                        :name="assign.assigned_team.name"
                        class="cursor-pointer -mr-1"
                        :username="assign.assigned_team.name.replace(/([0-9])/g, ' $1').trim()"
                        :background="forestreeHelpers.getAvatarColor(assign.assigned_team.name)"
                        color="#fff"
                        :size="30"
                        :customAvatarStyle="{
                            fontFamily: 'Arial',
                            fontSize: '12px',
                            'font-weight': 100                            
                        }"
                    >
                    </avatar>

                    <avatar
                        v-else-if="assign.assigned_to"
                        :title="assign.assigned_to.name"
                        :name="assign.assigned_to.name.replace(/([0-9])/g, ' $1').trim()"
                        class="cursor-pointer -mr-1"
                        :username="assign.assigned_to.name.replace(/([0-9])/g, ' $1').trim()"
                        :background="forestreeHelpers.getAvatarColor(assign.assigned_to.name)"
                        color="#fff"
                        :size="32"
                        :customAvatarStyle="{
                            fontFamily: 'Arial',
                            fontSize: '12px',
                            'font-weight': 100                            
                        }"
                    >
                    </avatar>
                </popper>
            </span>
            <span v-if="permissions && (permissions.work_can_assign || permitted)">
                <!--        Assigning user popper!        -->
                <popper
                    trigger="click"
                    :append-to-body="true"
                    ref="popover"
                >
                    <template #content="{ close }">
                        <div class="popper">
                            <div class="external-account-list">
                                <div v-if="teams && teams.length" class="bg-slate-200 text-slate-700 font-medium text-xs p-2">External</div>
                                <div v-if="teams && teams.length" class="py-2 max-h-64 overflow-y-auto">
                                    <!-- <div v-for="team in teams" :key="team.id" @click="onChange(null, team.id)" class="p-2 text-slate-600 hover:bg-blue-600 hover:text-white cursor-pointer">
                  {{ team.name }}
                  <div v-if="selectTeam === team.id">Are you sure?</div>
                </div> -->
                                    <div
                                        v-for="team in teams"
                                        :key="team.id"
                                        @click="selectTeam = team.id"
                                        class="p-2 text-slate-600 hover:bg-blue-600 hover:text-white cursor-pointer"
                                    >
                                        {{ team.name }}
                                        <div v-if="externalMsg" class="pt-2 text-xs">{{ externalMsg }}</div>
                                        <div v-if="!externalMsg && selectTeam === team.id" class="pt-2 text-xs">
                                            Send this job to <b>{{ team.name }}</b
                                            >?
                                        </div>
                                        <div v-if="!externalMsg && selectTeam === team.id" class="text-right pt-2">
                                            <button
                                                @click="
                                                    onChange(null, team.id);
                                                    close();
                                                "
                                                type="button"
                                                class="inline-flex items-center rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                            >
                                                Yes
                                            </button>
                                            <button
                                                @click="close()"
                                                type="button"
                                                class="inline-flex items-center rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                            >
                                                No
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="council-user-list">
                                <div class="bg-slate-200 text-slate-700 font-medium text-xs p-2">Users</div>
                                <div class="py-2 max-h-64 overflow-y-auto">
                                    <p
                                        v-for="user in users.assign_users"
                                        :key="user.id"
                                        @click="
                                            onChange(user.id, null);
                                            close();
                                        "
                                        class="p-2 text-slate-600 hover:bg-blue-600 hover:text-white cursor-pointer"
                                    >
                                        {{ user.name }}{{ user.company_name ? " (" + user.company_name + ")" : "" }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>

                    <avatar
                        class="cursor-pointer hover:bg-grey-300"
                        name="+"
                        background="#eee"
                        :size="32"
                        color="#bbb"
                        :customAvatarStyle="{ 'font-size': '30px' }"
                    >
                    </avatar>
                </popper>
            </span>
        </div>
        <div v-else class="flex flex-wrap items-center">
            <popper
                trigger="click"
                :options="{
                    placement: 'top',
                    modifiers: { offset: { offset: '0,10px' } },
                }"
            >
                <div class="popper">
                    <div class="p-4">
                        <p v-for="user in users.assign_users" :key="user.id" class="text-slate-600">
                            {{ user.name }}{{ user.company_name ? " (" + user.company_name + ")" : "" }}
                        </p>
                    </div>
                </div>
                <avatar
                    class="cursor-pointer hover:bg-grey-300"
                    name="+"
                    background="#eee"
                    :size="30"
                    color="#bbb"
                    :customAvatarStyle="{ 'font-size': '30px' }"
                >
                </avatar>
            </popper>
        </div>
        <template v-if="showAssignSelect">
            <div class="relative pl-2 py-2">
                <!-- <select
          @change="onChange($event)"
          class="block appearance-none w-full bg-slate-400 border border-slate-200
            text-slate-700 py-2 px-4 pr-8 rounded-sm leading-tight focus:outline-none
            focus:bg-white focus:border-slate-500" id="grid-state"
            >
          <option
            placeholder="user"
            selected
            disabled
            >
          </option>
          <option
            v-for="user in users.assign_users"
            :key="user.id"
            :value="user.id"
            >{{ user.name }}{{ user.company_name ? ' (' + user.company_name + ')' : '' }}
          </option>
        </select> -->
                <!-- <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-700">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
          </div> -->
            </div>
        </template>
        <div v-if="msg" class="px-2 pt-2">
            <p class="text-xs text-red-400">{{ msg }}</p>
        </div>
        <div v-if="externalMsg" class="px-2 pt-2">
            <p class="text-xs text-slate-400">{{ externalMsg }}</p>
        </div>
    </div>
</template>

<style>
.popper {
    min-width: 250px;
    max-width: 350px;
    text-align: left;
    padding: 0px;
    font-size: 15px;
    box-shadow: rgb(58 58 58 / 50%) 3px 3px 10px 1px;
    background: white;
}

.max-h-64 {
    max-height: 15rem;
}
</style>
